import { useState, useEffect } from 'react';
import { Search, Package, Maximize, Percent, User, Settings, FileText } from 'lucide-react';
import { getConfiguredProducts, performCalculation } from '../../api/calculator';
import { getCustomers } from '../../api/customers';

const TileCalculatorForm = ({ onCalculationComplete, onConfigureProduct }) => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    product_id: null,
    area_length_m: '',
    area_width_m: '',
    wastage_percent: 10,
    customer_id: null
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const loadProducts = async (search = '') => {
    try {
      setLoading(true);
      const response = await getConfiguredProducts({ search, limit: 50 });
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await getCustomers({ limit: 100 });
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    loadProducts(value);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ 
      ...prev, 
      product_id: product.id,
      wastage_percent: product.default_wastage_percent || 10
    }));
    setSearchTerm(product.name);
    setShowProductDropdown(false);
    setErrors(prev => ({ ...prev, product_id: null }));
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.product_id) {
      newErrors.product_id = 'Please select a product';
    }

    if (!formData.area_length_m || parseFloat(formData.area_length_m) <= 0) {
      newErrors.area_length_m = 'Please enter a valid length';
    }

    if (!formData.area_width_m || parseFloat(formData.area_width_m) <= 0) {
      newErrors.area_width_m = 'Please enter a valid width';
    }

    if (formData.wastage_percent < 0 || formData.wastage_percent > 50) {
      newErrors.wastage_percent = 'Wastage must be between 0 and 50%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setCalculating(true);
      const response = await performCalculation({
        title: formData.title,
        product_id: formData.product_id,
        area_length_m: parseFloat(formData.area_length_m),
        area_width_m: parseFloat(formData.area_width_m),
        wastage_percent: parseFloat(formData.wastage_percent),
        customer_id: formData.customer_id
      });

      if (response.success) {
        onCalculationComplete(response.data);
      }
    } catch (error) {
      console.error('Calculation failed:', error);
      setErrors({ submit: error.response?.data?.message || 'Calculation failed. Please try again.' });
    } finally {
      setCalculating(false);
    }
  };

  const clearProduct = () => {
    setSelectedProduct(null);
    setFormData(prev => ({ ...prev, product_id: null, wastage_percent: 10 }));
    setSearchTerm('');
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({ ...prev, customer_id: null }));
    setCustomerSearch('');
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  );

  const calculateArea = () => {
    const length = parseFloat(formData.area_length_m) || 0;
    const width = parseFloat(formData.area_width_m) || 0;
    return (length * width).toFixed(2);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="flex items-center gap-3 pb-4 border-b border-secondary-lightGray">
        <Package className="w-5 h-5 text-accent-gold" />
        <h2 className="text-lg font-semibold text-primary-black">Enter Calculation Details</h2>
      </div>

      {/* Calculation Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
          <FileText className="w-4 h-4 text-accent-gold" />
          Calculation Title (Optional)
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="e.g., Living Room Renovation"
          className="w-full px-4 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
        />
        <p className="text-xs text-primary-slate mt-1">A unique tag for this calculation</p>
      </div>

      {/* Product Selector */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
          <Package className="w-4 h-4 text-accent-gold" />
          Select Tile Product <span className="text-red-600">*</span>
        </label>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary-slate" />
            <input
              type="text"
              placeholder="Search by product name or code..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowProductDropdown(true)}
              className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                errors.product_id ? 'border-red-500' : 'border-secondary-lightGray'
              }`}
            />
            {selectedProduct && (
              <button 
                type="button" 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-secondary-lightGray rounded-full flex items-center justify-center text-primary-slate hover:bg-red-100 hover:text-red-600"
                onClick={clearProduct}
              >
                ×
              </button>
            )}
          </div>
          {errors.product_id && <p className="text-red-500 text-xs mt-1">{errors.product_id}</p>}

          {showProductDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-primary-slate">Loading products...</div>
              ) : products.length > 0 ? (
                products.map(product => (
                  <div
                    key={product.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer border-b border-secondary-lightGray last:border-0 hover:bg-secondary-offWhite ${
                      selectedProduct?.id === product.id ? 'bg-accent-gold/10' : ''
                    }`}
                    onClick={() => handleProductSelect(product)}
                  >
                    <span className="px-2 py-1 bg-accent-gold text-white text-xs font-bold rounded">
                      {product.product_code}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-primary-black truncate">{product.name}</p>
                      <p className="text-xs text-primary-slate">
                        {product.brand_name} • {product.size_name} • {product.pieces_per_carton} pcs/carton
                      </p>
                    </div>
                    <span className="font-semibold text-accent-gold whitespace-nowrap">
                      ₦{parseFloat(product.selling_price).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-primary-slate">
                  <p>No configured products found</p>
                  <p className="text-xs mt-1">Configure a product in the Products page first</p>
                  {onConfigureProduct && (
                    <button 
                      type="button" 
                      className="mt-2 flex items-center gap-2 mx-auto px-3 py-1.5 border border-accent-gold text-accent-gold rounded-lg text-sm hover:bg-accent-gold/10"
                      onClick={() => onConfigureProduct()}
                    >
                      <Settings className="w-4 h-4" /> Configure a Product
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected Product Display */}
        {selectedProduct && (
          <div className="mt-3 p-3 bg-accent-gold/5 border border-accent-gold/20 rounded-lg flex items-start gap-3">
            <span className="px-2 py-1 bg-accent-gold text-white text-xs font-bold rounded">
              {selectedProduct.unique_code}
            </span>
            <div>
              <h4 className="font-medium text-primary-black">{selectedProduct.name}</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-primary-slate">
                <span>Size: {selectedProduct.tile_length_mm}mm × {selectedProduct.tile_width_mm}mm</span>
                <span>Pieces/Carton: {selectedProduct.pieces_per_carton}</span>
                <span>Coverage/Carton: {parseFloat(selectedProduct.coverage_per_carton_sqm).toFixed(2)} m²</span>
                <span>Price/Carton: ₦{parseFloat(selectedProduct.selling_price).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Area Dimensions */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
            <Maximize className="w-4 h-4 text-accent-gold" />
            Room Length (meters) <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            name="area_length_m"
            value={formData.area_length_m}
            onChange={handleInputChange}
            placeholder="e.g., 5.5"
            step="0.01"
            min="0.1"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
              errors.area_length_m ? 'border-red-500' : 'border-secondary-lightGray'
            }`}
          />
          {errors.area_length_m && <p className="text-red-500 text-xs mt-1">{errors.area_length_m}</p>}
          <p className="text-xs text-primary-slate mt-1">Measure the longer side</p>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
            <Maximize className="w-4 h-4 text-accent-gold" />
            Room Width (meters) <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            name="area_width_m"
            value={formData.area_width_m}
            onChange={handleInputChange}
            placeholder="e.g., 4.0"
            step="0.01"
            min="0.1"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
              errors.area_width_m ? 'border-red-500' : 'border-secondary-lightGray'
            }`}
          />
          {errors.area_width_m && <p className="text-red-500 text-xs mt-1">{errors.area_width_m}</p>}
          <p className="text-xs text-primary-slate mt-1">Measure the shorter side</p>
        </div>
      </div>

      {/* Area Preview */}
      {formData.area_length_m && formData.area_width_m && (
        <div className="flex items-center justify-between p-4 bg-accent-gold/10 border border-accent-gold/30 rounded-lg">
          <span className="text-primary-slate">Total Area</span>
          <span className="text-2xl font-bold text-accent-gold">{calculateArea()} m²</span>
        </div>
      )}

      {/* Wastage Percentage */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
          <Percent className="w-4 h-4 text-accent-gold" />
          Wastage Percentage
        </label>
        <div className="relative w-32">
          <input
            type="number"
            name="wastage_percent"
            value={formData.wastage_percent}
            onChange={handleInputChange}
            min="0"
            max="50"
            step="1"
            className={`w-full px-4 py-3 pr-8 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-right ${
              errors.wastage_percent ? 'border-red-500' : 'border-secondary-lightGray'
            }`}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-slate">%</span>
        </div>
        {errors.wastage_percent && <p className="text-red-500 text-xs mt-1">{errors.wastage_percent}</p>}
        <p className="text-xs text-primary-slate mt-1">Recommended: 10% for standard, 15% for complex layouts</p>
      </div>

      {/* Customer Selector (Optional) */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
          <User className="w-4 h-4 text-accent-gold" />
          Customer (Optional)
        </label>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary-slate" />
            <input
              type="text"
              placeholder="Search customer by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onFocus={() => setShowCustomerDropdown(true)}
              className="w-full pl-10 pr-10 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
            {selectedCustomer && (
              <button 
                type="button" 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-secondary-lightGray rounded-full flex items-center justify-center text-primary-slate hover:bg-red-100 hover:text-red-600"
                onClick={clearCustomer}
              >
                ×
              </button>
            )}
          </div>

          {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredCustomers.slice(0, 10).map(customer => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 cursor-pointer border-b border-secondary-lightGray last:border-0 hover:bg-secondary-offWhite"
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <span className="font-medium text-primary-black">{customer.name}</span>
                  <span className="text-sm text-primary-slate">{customer.phone}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-primary-slate mt-1">Associate this calculation with a customer for tracking</p>
      </div>

      {/* Error Message */}
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {errors.submit}
        </div>
      )}

      {/* Submit Button */}
      <button 
        type="submit" 
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-accent-gold text-white rounded-lg font-bold text-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={calculating || !formData.product_id}
      >
        {calculating ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            Calculating...
          </>
        ) : (
          <>
            <Package className="w-5 h-5" />
            Calculate Tiles Needed
          </>
        )}
      </button>
    </form>
  );
};

export default TileCalculatorForm;
